import { FC, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { AppContext } from '../contexts/AppContext';
import { getContentType } from '../common/Common';
import { getTranslation } from '../common/Translation';
import MediaElement from './MediaElement';
import Link from './Link';
import useFetchDocument from '../hooks/useFetchDocument';

interface TimelineItemProps {
  documentId: number,
  display?: string,
  title?: string,
  subtitle?: string,
  image?: any,
  imageHeight?: number,
  itemWidth?: number,
  textAlign?: string,
  showLines?: boolean,
  textBackground?: "block" | "shadow" | "outline" |  "none" | undefined,
  backgroundColor?: string,
  borderRadius?: number
}

const Item = styled.li<{ $width?: number }>`
  float: left;
  overflow-y: visible;
  flex: 0 0 ${({ $width }) => $width ?? 25}vw;
  width: ${({ $width }) => $width ?? 25}vw;
  perspective: 1000;
  backface-visibility: hidden;
  transform: translateZ(0);

  &:active {
    filter: brightness(0.8);
  }

  &.noImage {
  }

  & .shadow {
    text-shadow: 0 1px 2px rgba(0,0,0,0.8), 0 2px 6px rgba(0,0,0,0.6), 0 0 12px rgba(0,0,0,0.4);
    background-color: inherit;
  }

  & .outline {
    text-shadow: -2px 0 0 #000, 2px 0 0 #000, 0 -2px 0 #000, 0 2px 0 #000, 0  2px 6px rgba(0,0,0,0.6);
    background-color: inherit;
  }

  & .none {
    background-color: inherit;
  }
`

const StyledImage = styled(MediaElement)<{ $height?: number, $borderRadius?: number }>`
  width: 100%;
  height: calc(${({ $height }) => $height ?? 30} * var(--timeline-vh, 1vh));
  object-fit: cover;
  border-radius: 8px;
  overflow: hidden;
  border-radius: ${({ $borderRadius }) => $borderRadius ?? 0}% !important;
`

const Captions = styled.div<{ $showLines?: boolean, $textAlign?: string }>`
  border-top: ${({ $showLines }) => ($showLines ? '2px solid #fff' : 'none')};
  height: calc(12 * var(--timeline-vh, 1vh));
  margin-top: calc(2.5 * var(--timeline-vh, 1vh));
  display: flex;
  flex-direction: column;
  align-items: ${({ $textAlign }) => $textAlign === "left" ? "flex-start" : $textAlign === "right" ? "flex-end" : "center"};
`;

const Title = styled.span`
  margin: 10px 0 0;
  color: ${p => p.theme.textColor};
  background-color: rgba(0,0,0,.4);
  font-size: 1.4em;
  font-weight: 700;
  text-shadow: 4px 4px 90px #000;
  border-radius: 1px;
  padding: 6px 12px;
  display: inline;
  max-width: 100%;
  line-height: 1.5em;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
`

const Subtitle = styled.span`
  margin: 15px 0 0;
  color: #fff;
  background-color: rgba(0,0,0,.3);
  font-size: 1.2em;
  font-weight: 700;
  text-shadow: 4px 4px 90px #000;
  border-radius: 2px;
  padding: 6px 12px;
  display: inline;
  max-width: 100%;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
`

/**
 * Render an item for the timeline view
 * @returns {JSX.Element} Component template
 */
const TimelineItem: FC<TimelineItemProps> = ({documentId, display, title, subtitle, showLines, image, imageHeight, itemWidth, textAlign, textBackground, backgroundColor, borderRadius}) => {
  const { currentLanguage } = useContext(AppContext);
  const [itemTitle, setItemTitle] = useState<any>(null);
  const [itemSubtitle, setItemSubtitle] = useState<any>(null);
  const [contentType, setContentType] = useState<string>("");
  const [data] = useFetchDocument(documentId);
  const [itemMedia, setItemMedia] = useState<any>(null);

  // Should custom image or document main image be used?
  useEffect(() => {
    const imageData = image?.mediaId ? image : (data?.content?.general?.image || data?.content?.background?.image)
    setItemMedia(imageData);
  }, [image, data])

  // Get item type
  useEffect(() => {
    setContentType(getContentType(data))
  }, [data, setContentType]);

  // Get title and subtitle
  useEffect(() => {
    if (data) {
      setItemTitle(getTranslation(title, currentLanguage)?.length > 0 ? getTranslation(title, currentLanguage) : getTranslation(data?.title, currentLanguage));
      setItemSubtitle(getTranslation(subtitle, currentLanguage)?.length > 0 ? getTranslation(subtitle, currentLanguage) : null);
    }
  }, [title, subtitle, currentLanguage, data]);

  // Do not display link if not available in current language
  if (data?.status && !(data?.title?.[currentLanguage || "no"] || data?.content?.content?.blocks?.[currentLanguage || "no"])) { return null; }

  // Only display if published
  if (data?.status && !data?.status?.includes("published")) { return null; }

  return (
    <Item className={(display === "text") ? "noImage" : ""} $width={itemWidth}>
      <Link id={documentId} presentationType={contentType}>
        <>
          {(!(display === "text")) && (<StyledImage media={contentType && itemMedia} backgroundColor={backgroundColor || `transparent`} $height={imageHeight} $borderRadius={borderRadius || 0}/>)}
          {!(display === "thumbnail") && (
            <Captions $showLines={showLines} $textAlign={textAlign}>
              {itemTitle && (<Title className={textBackground}>{itemTitle}</Title>)}
              {itemSubtitle && (<Subtitle className={textBackground}>{itemSubtitle}</Subtitle>)}
            </Captions>
          )}
        </>
      </Link>
    </Item>
  );
}

export default TimelineItem;
